import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import {
  formatNumToStandardNotation,
  getAggregatePeriodLabel,
} from '@modules/common/utils';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import {
  useGetDashboardAnalysisLinkWithState,
  useGetDashboardLinkWithGlobalFilterParams,
} from '@modules/dashboard/hooks';
import { CooccurrenceIcon } from '@modules/quantify/components/CooccurrenceIcon';
import {
  getAnomalousTimestampFromDataPoints,
  getFormattedAnomalousTimeRange,
} from '@modules/quantify/components/QuantifyAnomaliesTableView/helpers';
import { QUANTIFY_COOCCURRING_HELP_DESK_ARTICLE } from '@modules/quantify/constants';
import {
  useGetQuantifyAggregatePeriod,
  useIsAnomaliesTrendVisible,
  useIsCooccurrenceIconVisible,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  CompareReportDataPoint,
  QuantifyReportDataPoint,
} from '@modules/quantify/store/types';
import { EastRounded } from '@mui/icons-material';
import { Box, Button, Link, Stack, Typography, alpha } from '@mui/material';
import { AnalysisViewFragment } from 'generated/graphql';
import { uniqBy } from 'lodash';
import pluralize from 'pluralize';
import { useNavigate } from 'react-router-dom';

interface DashboardQuantifyChartBannerProps {
  isTableView: boolean;
  analysis: AnalysisViewFragment;
}

const hasQuantifyReportDataPoints = (
  dataPoints: QuantifyReportDataPoint[] | CompareReportDataPoint[],
): dataPoints is QuantifyReportDataPoint[] => {
  return [...dataPoints].every(
    (dataPoint: QuantifyReportDataPoint | CompareReportDataPoint) =>
      'value' in dataPoint,
  );
};

export const DashboardQuantifyChartBanner = ({
  isTableView,
  analysis,
}: DashboardQuantifyChartBannerProps) => {
  const navigate = useNavigate();

  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const shouldShowTopDataPoints = useQuantifyStore(
    ({ query }) => query.shouldShowTopDataPoints,
  );

  const aggregateHits = useQuantifyStore(({ visual }) =>
    isTableView ? visual.tableAggregateHits : visual.totalAggregateHits,
  );
  const aggregatePeriod = useGetQuantifyAggregatePeriod();
  const chartDataPoints = useQuantifyStore(
    ({ reportData, query: { compareQuery } }) => {
      if (compareQuery) {
        return isTableView
          ? reportData.compare.tableRows
          : reportData.compare.chartDataPoints;
      }

      return isTableView
        ? reportData.quantify.tableRows
        : reportData.quantify.chartDataPoints;
    },
  );

  const isAnomaliesTrendVisible = useIsAnomaliesTrendVisible();

  const isSingleRecordQuery = useIsSingleRecordQuery();

  const isCooccurrenceIconVisible = useIsCooccurrenceIconVisible({
    isTableView,
  });

  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const uniqueDataPointsCount = uniqBy<
    QuantifyReportDataPoint | CompareReportDataPoint
  >(chartDataPoints, 'id').length;

  const { analysisLink, analysisLinkState } =
    useGetDashboardAnalysisLinkWithState(analysis);

  const dashboardLinkWithGlobalFilters =
    useGetDashboardLinkWithGlobalFilterParams();

  if (isSingleRecordQuery) {
    return null;
  }

  const displayName = !!groupBy?.detail?.metadata
    ? getFormattedDisplayNameForEqlField({
        eqlField: groupBy,
        shouldRemoveFileUploadPrefix,
        isUnifiedFieldEnabled,
      })
    : pluralize(groupBy?.displayName ?? '', aggregateHits);

  const renderViewAllButton = () => {
    if (uniqueDataPointsCount >= aggregateHits) {
      return null;
    }

    return (
      <Button
        size="small"
        onClick={() => {
          window.history.replaceState(null, '', dashboardLinkWithGlobalFilters);
          navigate(analysisLink, { state: analysisLinkState });
          trackDashboardEvent({
            eventName: 'Dashboard Analysis - View All Button Clicked',
            dashboardLink: dashboardLinkWithGlobalFilters,
            analysisLink,
          });
        }}
        sx={{
          py: 0,
          textTransform: 'none',
          fontSize: (theme) => theme.typography.caption.fontSize,
          whiteSpace: 'nowrap',
          minWidth: 'max-content',
        }}
        endIcon={
          <EastRounded
            sx={{
              fontSize: '0.8rem !important',
            }}
          />
        }
      >
        View All
      </Button>
    );
  };

  const renderPrimaryText = () => {
    if (!isAnomaliesTrendVisible) {
      return (
        <>
          Showing {shouldShowTopDataPoints ? 'Top' : 'Selected'}{' '}
          {uniqueDataPointsCount} of{' '}
          {formatNumToStandardNotation(aggregateHits)} {displayName}
        </>
      );
    }

    const anomalousTimestamp =
      chartDataPoints && hasQuantifyReportDataPoints(chartDataPoints)
        ? getAnomalousTimestampFromDataPoints(chartDataPoints)
        : null;

    const aggregatePeriodLabel = getAggregatePeriodLabel(aggregatePeriod);

    const formattedAnomalousTimeRange = anomalousTimestamp
      ? getFormattedAnomalousTimeRange({
          anomalousTimestamp,
          aggregatePeriod,
        })
      : null;

    return (
      <>
        Showing {shouldShowTopDataPoints ? 'Top' : 'Selected'}{' '}
        {uniqueDataPointsCount} Anomalies for the {aggregatePeriodLabel}
        {formattedAnomalousTimeRange && (
          <>
            <Box component="span">:</Box>{' '}
            <Box
              component="span"
              sx={{
                color: 'error.light',
                fontWeight: 500,
              }}
            >
              {formattedAnomalousTimeRange}
            </Box>
          </>
        )}
      </>
    );
  };

  const renderSecondaryText = () => {
    if (!isCooccurrenceIconVisible) {
      return null;
    }

    return (
      <Stack direction="row" alignItems="flex-start" spacing={0.3}>
        <Box mt={-0.1}>
          <CooccurrenceIcon size={16} />
        </Box>
        <Box component="span">
          indicates co-occurrence.
          <Link
            href={QUANTIFY_COOCCURRING_HELP_DESK_ARTICLE}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            {' '}
            Learn more.
          </Link>
        </Box>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        py: 0.5,
        px: 2,
        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.05),
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {renderPrimaryText()}
          </Typography>
          {renderViewAllButton()}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {renderSecondaryText()}
        </Typography>
      </Stack>
    </Box>
  );
};
